import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Loader2, TrendingUp, TrendingDown, Copy, Calculator } from 'lucide-react';
import { TPSLCalculatorService } from '@/services/TPSLCalculatorService';
import { toast } from 'sonner';

interface QuickResults {
  symbol: string;
  currentPrice: number;
  tpPrice: number;
  slPrice: number;
  tpUsd: number;
  slUsd: number;
  ctraderLots: number;
  tvPositionValue: number;
  riskRewardRatio: number;
  priceSource: 'cache' | 'api';
}

export function QuickModeCalculator() {
  const [symbol, setSymbol] = useState('EURUSD');
  const [tpPercent, setTpPercent] = useState('2.0');
  const [slPercent, setSlPercent] = useState('1.0');
  const [tvPositionSize, setTvPositionSize] = useState('10000');
  const [results, setResults] = useState<QuickResults | null>(null);
  const [isCalculating, setIsCalculating] = useState(false);

  const calculateRisk = async () => {
    if (!symbol || !tpPercent || !slPercent || !tvPositionSize) {
      toast.error('Please fill in all fields');
      return;
    }

    setIsCalculating(true);
    try {
      const result = await TPSLCalculatorService.calculateTPSLFromPercentage({
        symbol: symbol.toUpperCase(),
        positionSize: parseFloat(tvPositionSize),
        tpPercent: parseFloat(tpPercent),
        slPercent: parseFloat(slPercent),
      });

      // Calculate TP and SL prices
      const tpPrice = result.currentPrice * (1 + result.tpPercent / 100);
      const slPrice = result.currentPrice * (1 - result.slPercent / 100);

      // Calculate Risk:Reward ratio
      const riskRewardRatio = result.tpUSD / result.slUSD;

      setResults({
        symbol: symbol.toUpperCase(),
        currentPrice: result.currentPrice,
        tpPrice: tpPrice,
        slPrice: slPrice,
        tpUsd: result.tpUSD,
        slUsd: result.slUSD,
        ctraderLots: result.ctraderLotSize,
        tvPositionValue: result.positionValueUSD,
        riskRewardRatio: riskRewardRatio,
        priceSource: 'api'
      });

      toast.success('Risk calculation completed');
    } catch (error) {
      console.error('Calculation error:', error);
      toast.error('Failed to calculate risk parameters');
    } finally {
      setIsCalculating(false);
    }
  };

  const copyToClipboard = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
      {/* Input Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calculator className="h-5 w-5 text-primary" />
            Input Parameters
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="symbol">Trading Symbol</Label>
            <Input
              id="symbol"
              placeholder="EURUSD, BTCUSD, XAUUSD..."
              value={symbol}
              onChange={(e) => setSymbol(e.target.value.toUpperCase())}
            />
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="tpPercent">Take Profit %</Label>
              <Input
                id="tpPercent"
                type="number"
                step="0.1"
                placeholder="2.0"
                value={tpPercent}
                onChange={(e) => setTpPercent(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="slPercent">Stop Loss %</Label>
              <Input
                id="slPercent"
                type="number"
                step="0.1"
                placeholder="1.0"
                value={slPercent}
                onChange={(e) => setSlPercent(e.target.value)}
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="tvPositionSize">TradingView Position Size</Label>
            <Input
              id="tvPositionSize"
              type="number"
              placeholder="10000"
              value={tvPositionSize}
              onChange={(e) => setTvPositionSize(e.target.value)}
            />
            <p className="text-xs text-muted-foreground">
              Number of units/shares/contracts in TradingView
            </p>
          </div>

          <Button 
            onClick={calculateRisk} 
            disabled={isCalculating}
            className="w-full"
          >
            {isCalculating ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Calculating...
              </>
            ) : (
              <>
                <Calculator className="h-4 w-4 mr-2" />
                Calculate Risk
              </>
            )}
          </Button>
        </CardContent>
      </Card>

      {/* Results Section */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <TrendingUp className="h-5 w-5 text-primary" />
            Results
          </CardTitle>
        </CardHeader>
        <CardContent>
          {results ? (
            <div className="space-y-4">
              {/* Symbol & Price Info */}
              <div className="flex items-center justify-between p-3 bg-muted/50 rounded-lg">
                <div>
                  <div className="font-medium">{results.symbol}</div>
                  <div className="text-sm text-muted-foreground">
                    Current: ${results.currentPrice.toFixed(5)}
                    <Badge variant="outline" className="ml-2 text-xs">
                      {results.priceSource === 'cache' ? 'Cached' : 'Live'}
                    </Badge>
                  </div>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => copyToClipboard(results.currentPrice.toString(), 'Current price')}
                >
                  <Copy className="h-3 w-3" />
                </Button>
              </div>

              {/* TP/SL Prices */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-green-500/10 border border-green-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <TrendingUp className="h-4 w-4 text-green-500" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.tpPrice.toFixed(5), 'TP Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="mt-1">
                    <div className="text-sm text-muted-foreground">Take Profit</div>
                    <div className="font-medium">${results.tpPrice.toFixed(5)}</div>
                  </div>
                </div>

                <div className="p-3 bg-red-500/10 border border-red-500/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <TrendingDown className="h-4 w-4 text-red-500" />
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.slPrice.toFixed(5), 'SL Price')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="mt-1">
                    <div className="text-sm text-muted-foreground">Stop Loss</div>
                    <div className="font-medium">${results.slPrice.toFixed(5)}</div>
                  </div>
                </div>
              </div>

              {/* USD Values */}
              <div className="grid grid-cols-2 gap-3">
                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">TP Profit</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`$${results.tpUsd.toFixed(2)}`, 'TP USD')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium text-green-600">${results.tpUsd.toFixed(2)}</div>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">SL Loss</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(`$${results.slUsd.toFixed(2)}`, 'SL USD')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium text-red-600">${results.slUsd.toFixed(2)}</div>
                </div>
              </div>

              {/* Trading Info */}
              <div className="space-y-3">
                <div className="p-3 bg-primary/5 border border-primary/20 rounded-lg">
                  <div className="flex items-center justify-between">
                    <div className="text-sm text-muted-foreground">cTrader Lot Size</div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => copyToClipboard(results.ctraderLots.toFixed(2), 'Lot size')}
                    >
                      <Copy className="h-3 w-3" />
                    </Button>
                  </div>
                  <div className="font-medium">{results.ctraderLots.toFixed(2)} lots</div>
                </div>

                <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
                  <div>
                    <div className="text-sm text-muted-foreground">Risk:Reward Ratio</div>
                    <div className="font-medium">1:{results.riskRewardRatio.toFixed(2)}</div>
                  </div>
                  <Badge variant={results.riskRewardRatio >= 2 ? "default" : "secondary"}>
                    {results.riskRewardRatio >= 2 ? "Good R:R" : "Low R:R"}
                  </Badge>
                </div>

                <div className="p-3 bg-muted/30 rounded-lg">
                  <div className="text-sm text-muted-foreground">TV Position Value</div>
                  <div className="font-medium">${results.tvPositionValue.toLocaleString()}</div>
                </div>
              </div>
            </div>
          ) : (
            <div className="text-center text-muted-foreground py-8">
              Enter your parameters and click Calculate Risk to see results
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}